import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import * as Yup from 'yup';
import ZRSwitchField from '@zr/components/Fields/ZRSwitchField';

import { salaries } from '@zr/common/js/profile/more-info-options';
import Alert from '@zr/components/Alert';
import LabeledField from '@zr/components/Fields/LabeledField';
import logEvent from '@zr/utilities/logEvent';
import { setExternalPreference } from '@zr/utilities/api/preference';
import buildProfileLocationString from '@zr/utilities/buildProfileLocationString';
import { RDB_OPT_IN_TOOLTIP } from '@zr/jobseeker-public/apps/jobseeker-screens/labels';

import ZipcodeAutocomplete from './ZipcodeAutocomplete/ZipcodeAutocomplete';

import './BasicInfoForm.less';

import {
  US,
  CA,
  COUNTRY_CODE_MAP,
  COUNTRY_NAME,
  COUNTRY_DISPLAY,
  ZIPCODE_NAME,
  SALARY_NAME,
  SALARY_DISPLAY,
  REMOTE_NAME,
  REMOTE_DISPLAY,
  LOCATION_NAME,
  getZipcodeDisplay,
  isUSorCA,
  calculateFormInitialValues,
  RDB_OPT_IN_NAME,
  RDB_DISPLAY,
} from '../utilities';

export function BasicInfoForm({
  header, initialValues, locationAutocompleteUrl, onSubmit, salaryNote, submitText, showRdbOptIn,
}) {
  const refLoggedFields = useRef(new Set());

  const logFieldOnce = (field) => {
    if (!refLoggedFields.current.has(field)) {
      logEvent('jobseeker_profile_fields_collection_data', {
        field,
      });
      refLoggedFields.current.add(field);
    }
  };

  const [autocompleteError, setAutocompleteError] = useState(false);

  const handleSuggestionsError = () => {
    setAutocompleteError(true);
  };

  const formInitialValues = calculateFormInitialValues(initialValues);

  const handleSave = (data, { setStatus, setSubmitting }) => {
    const { location, country, zipcode, ...rest } = data;
    if (autocompleteError) {
      setStatus(`Sorry, we were unable to save your ${getZipcodeDisplay(country)}.`);
    }
    // preference used in /apply/next-action
    setExternalPreference('contact', 'submitted_profile_key_fields', 'yes');
    // prioritize geoac's zipcode over inputted zipcode, though they should be the same
    // ZIPCODE_NAME also isn't intuitive to avoid autofillers
    delete rest[ZIPCODE_NAME];
    const {
      [SALARY_NAME]: minimumDesiredPay,
      [REMOTE_NAME]: willingnessToWorkRemotely,
      [RDB_OPT_IN_NAME]: makeProfilePublic,
    } = rest;
    let profileData;

    if (isUSorCA(country) && !autocompleteError) {
      // convert geoac location object into profile friendly fields
      profileData = {
        location: buildProfileLocationString({
          country: location.cc,
          city: location.city,
          state: location.admin1_code,
        }),
        city: location.city,
        state: location.admin1_code,
        country: location.cc,
        // prioritize geoac's zipcode over inputted zipcode, though they should be the same value
        zip_code: location.postal_code,
        ...rest,
      };
    } else {
      // if they selected Other country, do not save country or location at all
      profileData = rest;
    }
    // matches https://docs.google.com/spreadsheets/d/1GZfOsbY28NGIoFuXXAVBbrW9cSZ_1Y7zJscJlQbAL28/edit#gid=1633989742
    const logEventData = {
      minimum_desired_pay: minimumDesiredPay,
      willingness_to_work_remotely: willingnessToWorkRemotely ? 1 : 0,
      [RDB_OPT_IN_NAME]: makeProfilePublic, // moved into this form via ticket JS-9609
    };
    logEvent('jobseeker_profile_fields_collection_complete', logEventData);
    onSubmit(profileData);
    setSubmitting(false);
  };

  return (
    <Formik
      initialValues={formInitialValues}
      onSubmit={handleSave}
      // Yup.lazy is used to dynamically recalculate the schema and fresh zipcodeDisplay when values change
      // otherwise the schema and zipcodeDisplay grow stale when values are updated
      // https://github.com/formik/formik/issues/1228#issuecomment-640869857
      validationSchema={() => Yup.lazy((values) => {
        const validateZipcode = isUSorCA(values[COUNTRY_NAME]) && !autocompleteError;
        return Yup.object({
          [COUNTRY_NAME]: Yup.string().required(`${COUNTRY_DISPLAY} is required`),
          // location required if country is US or CA
          [ZIPCODE_NAME]: validateZipcode ? Yup.string().required(`${getZipcodeDisplay(values[COUNTRY_NAME])} is required`) : null,
          [LOCATION_NAME]: validateZipcode ? Yup.object({
            // with {} as the default value, validate a selected location by checking for a name property
            name: Yup.string().required(`Please enter a valid ${getZipcodeDisplay(values[COUNTRY_NAME])}`),
          }) : null,
        });
      })}
    >
      {({
        errors, isSubmitting, handleChange, handleSubmit, setValues, status, touched, values,
      }) => {
        const countryValue = values[COUNTRY_NAME];
        const showZipcodeAutocomplete = countryValue === US || countryValue === CA;

        // use event handlers rather than watching values with effect hooks, cause the latter messes up initial values rendering
        const handleCountryChange = (e) => {
          setValues({
            ...values,
            [LOCATION_NAME]: {},
            [ZIPCODE_NAME]: '',
            [COUNTRY_NAME]: e.target.value,
          });
          setAutocompleteError(false);
          logFieldOnce('country');
        };

        const handleZipcodeChange = (e) => {
          // reset their selected location if they've changed their input
          setValues({
            ...values,
            [LOCATION_NAME]: {},
            [ZIPCODE_NAME]: e.target.value,
          });
          setAutocompleteError(false);
          logFieldOnce('zipcode_postalcode');
        };

        const handleLocationSelect = (item) => {
          setValues({
            ...values,
            [LOCATION_NAME]: item.selectedItem,
            [ZIPCODE_NAME]: item.inputValue,
          });
          setAutocompleteError(false);
        };

        const handleSalaryChange = (e) => {
          handleChange(e);
          logFieldOnce('minimum_desired_pay');
        };

        const handleRemoteChange = (e) => {
          handleChange(e);
          logFieldOnce('work_remotely');
        };

        const handleRdbChange = (e) => {
          handleChange(e);
          // uses the same logging field as set in the former ActivePassiveStep (moved here by JS-9609)
          logFieldOnce('change_rdb_opt_in');
        };

        return (
          <>
            {status && (
              <Alert type="warning" message={status} />
            )}
            <form className="basic_info_form" onSubmit={handleSubmit}>
              <fieldset>
                <legend className="title">Basic Information</legend>
                {header && <p className="explanation">{header}</p>}
                <LabeledField
                  as="select"
                  id={COUNTRY_NAME}
                  input={{
                    disabled: isSubmitting,
                    name: COUNTRY_NAME,
                    onChange: handleCountryChange,
                    value: countryValue,
                  }}
                  label={COUNTRY_DISPLAY}
                  meta={{
                    error: errors[COUNTRY_NAME],
                    touched: touched[COUNTRY_NAME],
                  }}
                  placeholder={COUNTRY_DISPLAY}
                >
                  {Object.entries(COUNTRY_CODE_MAP).map(([countryKey, countryDisplay]) => (
                    <option key={countryKey} value={countryKey}>{countryDisplay}</option>
                  ))}
                </LabeledField>
                {showZipcodeAutocomplete && (
                  <ZipcodeAutocomplete
                    countryValue={countryValue}
                    disabled={isSubmitting}
                    inputError={errors[ZIPCODE_NAME]}
                    inputValue={values[ZIPCODE_NAME]}
                    locationAutocompleteUrl={locationAutocompleteUrl}
                    locationError={errors[LOCATION_NAME]?.name}
                    name={ZIPCODE_NAME}
                    onChange={handleZipcodeChange}
                    onSelectedItemChange={handleLocationSelect}
                    onSuggestionsError={handleSuggestionsError}
                    touched={touched[ZIPCODE_NAME]}
                  />
                )}
                <LabeledField
                  as="select"
                  className="salary_floated_label_select"
                  label={SALARY_DISPLAY}
                  input={{
                    disabled: isSubmitting,
                    name: SALARY_NAME,
                    onChange: handleSalaryChange,
                    value: values[SALARY_NAME],
                  }}
                  aria-describedby="salary_note"
                  placeholder={SALARY_DISPLAY}
                >
                  {salaries.map((salary) => <option key={salary.value} value={salary.value}>{salary.text}</option>)}
                </LabeledField>
                <p id="salary_note" className="salary_note">
                  {salaryNote}
                </p>
                <div className="remote_wrapper">
                  <input
                    checked={values[REMOTE_NAME]}
                    className="remote_checkbox"
                    disabled={isSubmitting}
                    id={REMOTE_NAME}
                    name={REMOTE_NAME}
                    onChange={handleRemoteChange}
                    type="checkbox"
                  />
                  <label className="remote_label" htmlFor={REMOTE_NAME}>
                    {REMOTE_DISPLAY}
                    <button
                      type="button"
                      className="remote_tip"
                      data-callout="tooltip"
                      title="Checking this box will allow you to be matched with remote friendly jobs outside of your current location."
                    >
                      More Info
                    </button>
                  </label>
                </div>
                {showRdbOptIn && (
                  <div className="rdb_wrapper">
                    <ZRSwitchField
                      label={RDB_DISPLAY}
                      disabled={isSubmitting}
                      input={{
                        name: RDB_OPT_IN_NAME,
                        onChange: handleRdbChange,
                        value: values[RDB_OPT_IN_NAME],
                      }}
                      tooltip={RDB_OPT_IN_TOOLTIP}
                      buttonLeftOfLabel
                    />
                  </div>
                )}
                <div className="button_wrapper">
                  <button className="continue_button" disabled={isSubmitting} type="submit">{submitText}</button>
                </div>
              </fieldset>
            </form>
          </>
        );
      }}
    </Formik>
  );
}

BasicInfoForm.propTypes = {
  header: PropTypes.string,
  initialValues: PropTypes.shape({
    country: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    location: PropTypes.string,
    zipcode: PropTypes.string,
    desiredSalary: PropTypes.string,
    willWorkRemotely: PropTypes.bool,
    rdbOptIn: PropTypes.bool,
  }),
  locationAutocompleteUrl: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  salaryNote: PropTypes.node,
  submitText: PropTypes.string,
  showRdbOptIn: PropTypes.bool,
};

BasicInfoForm.defaultProps = {
  header: '',
  initialValues: {},
  salaryNote: (
    <>
      {'We\'ll use this information to match you with the most relevant jobs. '}
      <strong>This will not be shared with employers.</strong>
    </>
  ),
  submitText: 'Continue',
  showRdbOptIn: false,
};

export default BasicInfoForm;
